/*
 * Auflösung von Binding-Keys.
 *
 * Ein Widget bindet an einen String, nicht an ein Objekt — das ist Absicht:
 * ein `HmiProject` ist portables JSON, das aus der Cloud in ein Panel oder
 * ein Gateway-Image wandert, und ein String übersteht jede dieser Kopien.
 * Die Frage ist nur, WAS in dem String steht.
 *
 * Es gibt genau zwei Formen, und die produktive ist die erste:
 *
 *   1. LIVE — `{plantId}/{leafId}`, beide Guids im `D`-Format (8-4-4-4-12,
 *      ohne Klammern) und kleingeschrieben. Das ist die kanonische Form.
 *      Sie trägt die plantId mit, weil der Live-Pfad plant-scoped abonniert
 *      (`realtime/signalStore.ts`: eine Hub-Subscription pro Anlage) und der
 *      Command-Endpoint plant-scoped ist
 *      (`POST /api/plants/{plantId}/leaves/{leafId}/commands/set`). Ein
 *      nackter Leaf-Guid würde beides nicht ermöglichen, ohne dass jedes
 *      Widget zusätzlich die Anlage aus dem Kontext zieht.
 *
 *   2. DEMO — `demo/...`, alles was mit `demo/` beginnt. Das ist der
 *      SONDERFALL: nur der Simulator der Widget-Katalog-Seite
 *      (`pages/HmiPreviewPage.tsx`) benutzt ihn. Ein Live-Adapter darf einen
 *      Demo-Key niemals auflösen — er würde ins Leere abonnieren und der
 *      Renderer zeigte für immer einen Spinner, was von "Anlage antwortet
 *      nicht" nicht zu unterscheiden ist.
 *
 * Kleinschreibung: der Server-Decoder für MQTT-Topics ist strikt auf
 * lowercase-Hex-Guids (`docs/hvacloud-client-spec.md` §5.2, Beleg
 * `HvacloudV1MessageDecoder.cs:30`). Damit hier nichts driftet, ist
 * lowercase auch für Binding-Keys die kanonische Schreibweise:
 * {@link formatBindingKey} erzeugt ausschließlich lowercase.
 * {@link parseBindingKey} akzeptiert allerdings BEIDE Schreibweisen und
 * normalisiert — ein von Hand oder von einem LLM geschriebenes Projekt mit
 * Uppercase-Guids soll funktionieren und nicht still nichts anzeigen. Wer
 * die strikte Prüfung für einen Validator braucht, nimmt
 * {@link isCanonicalBindingKey}.
 *
 * Dieses Modul ist absichtlich frei von Laufzeit-Importen (nur Typen), damit
 * es ohne Bundler direkt testbar bleibt — siehe `tests/bindingKey.test.ts`.
 */

/** Aufgelöster Live-Binding-Key. Beide Felder sind kleingeschriebene Guids. */
export interface PointBindingKey {
  plantId: string;
  leafId: string;
}

/** Präfix des Simulator-Namensraums. Siehe Modul-Header, Form 2. */
export const DEMO_KEY_PREFIX = 'demo/';

/** Guid im `D`-Format, case-insensitiv. Bewusst kein `RegExp`-Literal mit
 *  `g`-Flag: `lastIndex` würde über Aufrufe hinweg wandern. */
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const GUID_PATTERN_LOWER = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * Baut den kanonischen Live-Binding-Key. Immer lowercase.
 *
 * Wirft nicht: eine leere oder unsinnige Eingabe erzeugt einen Key, den
 * {@link parseBindingKey} dann ablehnt. Der Fehler soll an der Stelle
 * auftauchen, an der jemand ihn auch sehen kann (im Adapter, als
 * `quality: 'bad'`), nicht in einem Layout-Editor-Klick.
 */
export function formatBindingKey(plantId: string, leafId: string): string {
  return `${plantId.trim().toLowerCase()}/${leafId.trim().toLowerCase()}`;
}

/**
 * Zerlegt einen Binding-Key in Anlage + Datenpunkt.
 *
 * Gibt `null` zurück für alles, was kein Live-Key ist — insbesondere für
 * Demo-Keys, für nackte Leaf-Guids ohne Anlage, und für Keys mit mehr oder
 * weniger als zwei Segmenten. Aufrufer müssen `null` behandeln; ein
 * Live-Adapter macht daraus einen Wert der Qualität `bad`, damit der
 * Renderer sichtbar meldet "hier kommt nichts" statt zu warten.
 */
export function parseBindingKey(raw: string | null | undefined): PointBindingKey | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  // Demo-Keys sind explizit kein Live-Key. Ohne diesen Riegel würde ein
  // Segment-Zähler sie ohnehin verwerfen, aber der frühe Ausstieg macht die
  // Absicht lesbar und schützt gegen einen künftigen `demo/{guid}/{guid}`.
  if (isDemoBindingKey(trimmed)) return null;

  const segments = trimmed.split('/');
  if (segments.length !== 2) return null;
  const [plantId, leafId] = segments;
  if (!GUID_PATTERN.test(plantId) || !GUID_PATTERN.test(leafId)) return null;

  return { plantId: plantId.toLowerCase(), leafId: leafId.toLowerCase() };
}

/**
 * Strikte Prüfung: ist der Key schon in kanonischer Form (lowercase)?
 *
 * Für Validierung beim Speichern eines `HmiProject` gedacht, damit
 * gespeicherte Projekte einheitlich aussehen. Nicht für den Lesepfad —
 * dort ist {@link parseBindingKey} tolerant, siehe Modul-Header.
 */
export function isCanonicalBindingKey(raw: string | null | undefined): boolean {
  if (typeof raw !== 'string') return false;
  const segments = raw.split('/');
  if (segments.length !== 2) return false;
  return GUID_PATTERN_LOWER.test(segments[0]) && GUID_PATTERN_LOWER.test(segments[1]);
}

/** Gehört der Key dem Simulator-Namensraum? Siehe Modul-Header, Form 2. */
export function isDemoBindingKey(raw: string | null | undefined): boolean {
  return typeof raw === 'string' && raw.trim().toLowerCase().startsWith(DEMO_KEY_PREFIX);
}

/**
 * Sammelt jeden Binding-Key aus einer Widget-Definition.
 *
 * Konvention im Definitionsmodell (`hmi/widgets.ts`): jedes Feld, dessen
 * Name auf `Point` endet, ist ein Binding-Key; `TrendWidget.series[].point`
 * und `SchematicOverlayWidget.anims[].valuePoint` sind die zwei
 * verschachtelten Ausnahmen, `EnergyFlowWidget.links[].valuePoint` und
 * `StageCascadeWidget.stages[]` die anderen zwei. Statt jedes Widget-Kind
 * einzeln aufzuzählen (22 Kinds, jedes neue würde vergessen) läuft hier ein
 * flacher rekursiver Walk über das JSON und nimmt alles mit, was der
 * Namenskonvention entspricht. Ein Widget-Kind, das morgen dazukommt, ist
 * damit automatisch erfasst.
 *
 * Verwendet von der produktiven Route, um zu bestimmen, welche Anlagen ein
 * Screen überhaupt braucht, und von der Diagnose-Anzeige "N von M Bindings
 * auflösbar".
 */
export function collectBindingKeys(widget: unknown): string[] {
  const found: string[] = [];
  walk(widget, found, 0);
  // Duplikate raus: mehrere Widgets binden oft denselben Punkt (Vorlauf
  // erscheint in Gauge, Trend und Compound-Tile).
  return Array.from(new Set(found));
}

/** Rekursionstiefe. Ein Widget ist flach; die Grenze fängt nur zyklische
 *  oder absurd verschachtelte Fremd-JSONs ab. */
const MAX_WALK_DEPTH = 8;

function walk(node: unknown, out: string[], depth: number): void {
  if (depth > MAX_WALK_DEPTH || node === null || typeof node !== 'object') return;
  if (Array.isArray(node)) {
    for (const item of node) walk(item, out, depth + 1);
    return;
  }
  const kind = (node as { kind?: unknown }).kind;
  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    // Die Eintraege der Meldungsliste nennen mit `point` einen Namen unter
    // `points`, keine Adresse; die Adressen stehen in `points` selbst.
    if (kind === 'alarm-list' && key === 'entries') continue;
    // Benannte Punkte der freien Bausteine: Name -> Adresse.
    // Teilschemen: Feldkennung -> Adresse, wie `points`.
    if ((key === 'points' || (kind === 'schematic-part' && key === 'bindings'))
        && value !== null && typeof value === 'object' && !Array.isArray(value)) {
      for (const v of Object.values(value as Record<string, unknown>)) {
        if (typeof v === 'string' && v.trim().length > 0) out.push(v);
      }
      continue;
    }
    // Schreibaktionen ({type: 'write', point: 'push'}) nennen mit `point`
    // einen Namen unter `points` des Widgets, keine Adresse.
    if (key === 'point' && (node as { type?: unknown }).type === 'write') continue;
    if (isBindingField(key, value)) {
      out.push(value as string);
      continue;
    }
    // Listen von Bindungen: Feldname auf `Points` (Monatszaehler der
    // Monats- und Jahresbalken, heatPoints/elPoints/aPoints/bPoints).
    if (key.endsWith('Points') && Array.isArray(value)) {
      for (const v of value) if (typeof v === 'string' && v.trim().length > 0) out.push(v);
      continue;
    }
    walk(value, out, depth + 1);
  }
}

/**
 * Ist dieses Feld eine Bindung mit Inhalt?
 *
 * Zwei Namensformen: alles, was auf `Point` endet, plus `TrendSeries.point`,
 * die einzige Stelle, an der das Feld im Plural-Kontext `series` schlicht
 * `point` heisst.
 *
 * <b>Der leere String zaehlt NICHT.</b> Das ist keine Feinheit: "nicht
 * gebunden" ist im gespeicherten Projekt der leere String, weil die
 * Pflichtfelder der Widget-Typen `string` und nicht optional sind (siehe
 * `UNBOUND` im Widget-Katalog des Editors). Wer den leeren String mitzaehlt,
 * meldet ein frisch abgelegtes Widget als Bindung, die sich nicht aufloesen
 * laesst. Auf der produktiven HMI-Seite steht dann "kaputt", wo "noch nicht
 * eingestellt" richtig waere, und das sind zwei verschiedene Befunde: der eine
 * verlangt Suche, der andere nur Weiterarbeiten. Gefunden am 2026-09-16 beim
 * Bauen des Editors.
 */
function isBindingField(key: string, value: unknown): boolean {
  if (typeof value !== 'string' || value.trim().length === 0) return false;
  return key.endsWith('Point') || key === 'point';
}

/**
 * Teilt eine Menge Binding-Keys nach Anlage auf.
 *
 * Ein Screen bindet im Normalfall genau eine Anlage; ein von Hand
 * zusammengestelltes Übersichts-Board kann mehrere binden. Die produktive
 * Route braucht diese Aufteilung, weil jede Anlage ihr eigenes Abo und
 * ihren eigenen Werte-Snapshot hat.
 *
 * Nicht auflösbare Keys landen in `unresolved` — sichtbar machen statt
 * wegwerfen, sonst fehlen im HMI Kacheln ohne Begründung.
 */
export function groupBindingKeysByPlant(keys: readonly string[]): {
  byPlant: Map<string, string[]>;
  unresolved: string[];
} {
  const byPlant = new Map<string, string[]>();
  const unresolved: string[] = [];
  for (const key of keys) {
    const parsed = parseBindingKey(key);
    if (!parsed) {
      unresolved.push(key);
      continue;
    }
    const bucket = byPlant.get(parsed.plantId);
    if (bucket) bucket.push(parsed.leafId);
    else byPlant.set(parsed.plantId, [parsed.leafId]);
  }
  return { byPlant, unresolved };
}
